import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, FilePdf } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SaldoPanel from "@/components/medicao/SaldoPanel";
import AuthImage from "@/components/medicao/AuthImage";
import FotosMapa from "@/components/medicao/FotosMapa";
import { StatusBadge, errMsg, fmtBRL, fmtDate, fmtNum, openPdf } from "@/lib/medicoes";

function MedicoesList({ items, base }) {
  if (!items.length) return <p className="text-sm text-[#94A3B8]">Nenhuma medição registrada.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#1E293B] text-left"><tr>
          {["Nº", "Período", "Frente", "Valor", "Versão", "Situação", "Relatório"].map((h) => <th key={h} className="px-4 py-3 uppercase-label !text-[10px]">{h}</th>)}
        </tr></thead>
        <tbody>
          {items.map((m, i) => (
            <tr key={m.id} className="border-t border-[#334155]" data-testid={`contrato-med-row-${i}`}>
              <td className="px-4 py-3"><Link to={`${base}/${m.id}`} className="font-mono text-[#FFDF00] hover:underline">nº {m.numero}</Link></td>
              <td className="px-4 py-3 text-[#94A3B8]">{fmtDate(m.periodo_inicio)} a {fmtDate(m.periodo_fim)}</td>
              <td className="px-4 py-3 text-[#94A3B8]">{m.frente_servico || "—"}</td>
              <td className="px-4 py-3 font-mono">{fmtBRL(m.valor_total)}</td>
              <td className="px-4 py-3 font-mono">v{m.versao}</td>
              <td className="px-4 py-3"><StatusBadge status={m.status} valorPago={m.valor_pago} /></td>
              <td className="px-4 py-3">
                <button onClick={() => openPdf(`/medicoes/${m.id}/relatorio-fotografico`)} className="text-[#94A3B8] hover:text-[#FFDF00]" title="Relatório fotográfico"><FilePdf size={16} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FornecedorContratoDetalhe() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get(`/fornecedor/contratos/${id}`).then((r) => setD(r.data))
    .catch((e) => toast.error(errMsg(e, "Contrato indisponível"))), [id]);
  useEffect(() => { load(); }, [load]);

  const novaMedicao = async () => {
    setCreating(true);
    try {
      const token = `${id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { data } = await api.post(`/fornecedor/contratos/${id}/medicoes`, { client_token: token, itens: [] });
      toast.success(`Medição nº ${data.numero} criada como rascunho`);
      nav(`/fornecedor/medicoes/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); setCreating(false); }
  };

  if (!d) return <div className="text-[#94A3B8]">Carregando…</div>;
  const c = d.contrato;
  const fotos = d.arquivos.filter((a) => a.tipo === "foto");
  const docs = d.arquivos.filter((a) => a.tipo === "documento");
  return (
    <div className="space-y-6 max-w-7xl" data-testid="fornecedor-contrato-detalhe">
      <Link to="/fornecedor/contratos" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#FFDF00]"><ArrowLeft size={14} /> Meus contratos</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="uppercase-label">Contrato nº</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2 gold-text" data-testid="fcd-numero">{c.numero}</h1>
          <p className="text-[#E9EEF5] mt-3">{c.objeto}</p>
          <p className="text-xs text-[#94A3B8] mt-2">{c.orgao || "—"} · {c.municipio_nome} · {c.modalidade} · Vigência {fmtDate(c.data_inicio)} a {fmtDate(c.data_fim)} · {c.status}</p>
        </div>
        <button data-testid="nova-medicao-btn" onClick={novaMedicao} disabled={creating || !d.planilha.length}
                title={!d.planilha.length ? "Contrato sem planilha de itens — solicite o cadastro ao gestor" : ""}
                className="px-5 py-3 rounded-md bg-[#FFDF00] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-40">
          <Plus size={16} weight="bold" /> {creating ? "Criando…" : "Nova medição"}
        </button>
      </div>
      {!d.planilha.length && <div className="tce-card p-4 text-sm text-[#F59E0B]" data-testid="sem-planilha">Este contrato ainda não possui planilha orçamentária de itens. Solicite ao gestor o cadastro dos itens para elaborar medições.</div>}
      <SaldoPanel resumo={d.resumo} />
      <Tabs defaultValue="medicoes">
        <TabsList className="bg-[#1E293B] flex-wrap h-auto">
          <TabsTrigger value="medicoes" data-testid="tab-medicoes">Medições ({d.medicoes.length})</TabsTrigger>
          <TabsTrigger value="planilha" data-testid="tab-planilha">Planilha</TabsTrigger>
          <TabsTrigger value="fotos" data-testid="tab-fotos">Fotos ({fotos.length})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Documentos ({docs.length})</TabsTrigger>
          <TabsTrigger value="evolucao" data-testid="tab-evolucao">Evolução do saldo</TabsTrigger>
          <TabsTrigger value="aditivos" data-testid="tab-aditivos">Aditivos ({d.aditivos.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="medicoes" className="tce-card p-4 mt-4"><MedicoesList items={d.medicoes} base="/fornecedor/medicoes" /></TabsContent>
        <TabsContent value="planilha" className="tce-card p-4 mt-4 overflow-x-auto">
          <table className="w-full text-xs"><thead className="bg-[#1E293B] text-left"><tr>
            {["Item", "Descrição", "Un", "Contratado", "Executado", "Em trâmite", "Saldo", "PU"].map((h) => <th key={h} className="px-3 py-2.5 uppercase-label !text-[10px]">{h}</th>)}
          </tr></thead><tbody>
            {d.planilha.map((p) => (
              <tr key={p.numero_item} className="border-t border-[#334155]">
                <td className="px-3 py-2 font-mono">{p.numero_item}</td><td className="px-3 py-2">{p.descricao}</td><td className="px-3 py-2">{p.unidade_medida}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtNum(p.quantidade_contratada, 4)}</td><td className="px-3 py-2 font-mono text-right">{fmtNum(p.quantidade_anterior, 4)}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtNum(p.quantidade_em_tramite, 4)}</td><td className="px-3 py-2 font-mono text-right text-[#22C55E]">{fmtNum(p.saldo_quantidade, 4)}</td>
                <td className="px-3 py-2 font-mono text-right">{fmtBRL(p.valor_unitario)}</td>
              </tr>))}
          </tbody></table>
        </TabsContent>
        <TabsContent value="fotos" className="tce-card p-4 mt-4">
          {!fotos.length && <p className="text-sm text-[#94A3B8]">Nenhuma fotografia.</p>}
          <div className="mb-4"><FotosMapa fotos={fotos} /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {fotos.map((f) => (
              <div key={f.id}><AuthImage arquivoId={f.id} alt={f.legenda} className="w-full h-32 object-cover rounded" />
                <div className="text-[11px] mt-1 truncate">{f.legenda || f.original_filename}</div></div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="docs" className="tce-card p-4 mt-4">
          {!docs.length && <p className="text-sm text-[#94A3B8]">Nenhum documento.</p>}
          {docs.map((x) => <div key={x.id} className="text-sm py-1.5 border-b border-[#334155]/50">{x.original_filename} <span className="text-[10px] text-[#64748B]">· {fmtDate(x.created_at)}</span></div>)}
        </TabsContent>
        <TabsContent value="evolucao" className="tce-card p-4 mt-4">
          {d.evolucao_saldo.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={d.evolucao_saldo}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="data" stroke="#94A3B8" fontSize={11} /><YAxis stroke="#94A3B8" fontSize={11} />
                <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: "#0F172A", border: "1px solid #334155" }} />
                <Line type="monotone" dataKey="saldo" stroke="#22C55E" name="Saldo" />
                <Line type="monotone" dataKey="executado_acumulado" stroke="#FFDF00" name="Executado acumulado" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-[#94A3B8]">Sem movimentações financeiras registradas.</p>}
        </TabsContent>
        <TabsContent value="aditivos" className="tce-card p-4 mt-4">
          {!d.aditivos.length && <p className="text-sm text-[#94A3B8]">Nenhum aditivo.</p>}
          {d.aditivos.map((a, i) => <div key={i} className="text-sm py-1.5 border-b border-[#334155]/50">Aditivo {a.numero} · {a.tipo} · {fmtBRL(a.valor_adicional)} {a.novo_prazo_fim && `· novo prazo ${fmtDate(a.novo_prazo_fim)}`}</div>)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { MedicoesList };
